/**
 * ⟦C57 · lot 3 · A-174⟧ **Le récapitulatif des jours, en abrégé : ce qui reste à l'écran, ce qui
 * passe derrière « Les dessous », et ce qui rejoint le Journal.**
 *
 * Le récap est le dialogue le PLUS RÉPÉTÉ de la partie (46 passages en 242 actions, 129 mots
 * visibles avant ce chantier) : c'est lui qui fait « on ne lit plus rien à la fin ». La règle
 * d'A-174 le ramène à : un titre, une phrase (`chapoDuRecap`), les soldes en nombres
 * (`soldesVisibles`, A-16 : le solde ne vit qu'AU-DESSUS du détail qui le décompose, et le détail
 * est à un bouton de là), et un bouton « Les dessous ».
 *
 * ## Rien ne disparaît
 *
 * `blocsDuRecap` rend le calcul COMPLET, ligne par ligne, dans les trois sections de l'écran
 * d'avant (ce que ça t'a coûté / pendant ce temps / au bout du compte). C'est une donnée pure
 * (chaînes et tableaux) : le dialogue « Les dessous » de la pop-up la met en page, et
 * `game/journal.ts` en fait le `detail` d'une entrée du Journal (`entreeDuRecap`), qui survit donc
 * au F5 et se relit plus tard, quand le dialogue est refermé depuis longtemps.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne calcule rien : chaque delta est celui que le moteur a MESURÉ (`RecapLigne.delta`), chaque
 * phrase vient de `game/recap.ts` (le vocabulaire fermé). Pur : aucun DOM, aucun store.
 */

import {
  soldeParJauge,
  type ActionType,
  type RecapJauge,
  type RecapLigne,
  type Recapitulatif,
} from "@maxime/shared";

import { jourSimule, nombre } from "./format";
import type { BlocSource } from "./journal";
import {
  ICONE_CAUSE,
  JAUGE_RECAP,
  deltaLisible,
  detailLisible,
  libelleCause,
  parJourCroissant,
  phraseNarrative,
  sansReputation,
  sectionner,
} from "./recap";

/**
 * Sans action connue (le Journal est écrit par un client qui ne la transmet pas toujours),
 * `cout_action` retombe sur « l'action » : le défaut de `libelleCause`, jamais une phrase inventée.
 */
const ACTION_NEUTRE: ActionType = "choix_evenement";

/** Le seuil écarte le bruit d'arrondi des jauges fractionnaires (momentum, argent). */
const SEUIL_SOLDE = 0.005;

/** Les soldes à montrer : ceux qui ont réellement bougé, dans l'ordre du moteur. */
export function soldesVisibles(recap: Recapitulatif): [RecapJauge, number][] {
  // ⟦C60 bis⟧ La réputation ne se lit plus (arbitrage n° 2) : ni solde, ni puce, ni ligne du Journal.
  const soldes = soldeParJauge(sansReputation(recap));
  return (Object.entries(soldes) as [RecapJauge, number][]).filter(([, v]) => Math.abs(v) >= SEUIL_SOLDE);
}

/** Le titre de la fenêtre : « Un jour a passé », « 3 jours ont passé » (4 mots au plus). */
export function titreDuRecap(joursTraverses: number): string {
  return joursTraverses === 1 ? "Un jour a passé" : `${nombre(joursTraverses)} jours ont passé`;
}

/** L'UNE phrase de la pop-up : « J1 à J4 · 12 h de travail. » (« Le monde n'a pas attendu » est dans les dessous.) */
export function chapoDuRecap(recap: Recapitulatif): string {
  const heures = recap.clock_fin - recap.clock_debut;
  return `J${jourSimule(recap.clock_debut)} à J${jourSimule(recap.clock_fin)} · ${nombre(heures)} h de travail.`;
}

/** Les puces d'un solde : « +45 👥 », en nombres et en icônes, jamais en phrase. */
export function puceDeSolde(jauge: RecapJauge, valeur: number): string {
  return `${deltaLisible(jauge, valeur)} ${JAUGE_RECAP[jauge].icone}`;
}

/**
 * Ce que le temps a pris et que les soldes ne disent pas : une idée périmée, une fenêtre refermée.
 * C'est la « prévenance » d'A-15 (le récap est le seul endroit où le joueur apprend que le temps coûte) :
 * elle reste à l'écran, en icône et en nombre, le nom de l'idée ou de la fenêtre est dans « Les dessous ».
 */
export function pertesDuTemps(recap: Recapitulatif): { icone: string; texte: string }[] {
  const compte = (cause: RecapLigne["cause"]) => recap.lignes.filter((l) => l.cause === cause).length;
  const pertes: { icone: string; texte: string }[] = [];
  const idees = compte("idee_perimee");
  if (idees > 0) pertes.push({ icone: "🗑", texte: `${nombre(idees)} idée${idees > 1 ? "s" : ""} périmée${idees > 1 ? "s" : ""}` });
  const fenetres = compte("opportunite_expiree");
  if (fenetres > 0) {
    pertes.push({ icone: "🚪", texte: `${nombre(fenetres)} fenêtre${fenetres > 1 ? "s" : ""} refermée${fenetres > 1 ? "s" : ""}` });
  }
  return pertes;
}

/** Les trois jauges dont le Journal garde le solde dans sa ligne : abonnés, énergie, santé mentale. */
const JAUGES_DE_LA_LIGNE: readonly RecapJauge[] = ["abonnes", "energie", "sante_mentale"];

/**
 * La ligne du Journal : « 3 jours ont passé · +45 👥 · −20 ⚡ ». Le titre, puis au plus les trois
 * soldes qui comptent (le reste est dans « Les dessous » de l'entrée).
 */
export function ligneDuRecap(recap: Recapitulatif): string {
  const soldes = soldesVisibles(recap);
  const bouts = JAUGES_DE_LA_LIGNE.flatMap((j) => {
    const trouve = soldes.find(([jauge]) => jauge === j);
    return trouve ? [puceDeSolde(trouve[0], trouve[1])] : [];
  });
  return [titreDuRecap(recap.jours_traverses), ...bouts].join(" · ");
}

/** Une ligne du moteur, en texte : la forme chiffrée ou la forme narrative (A-16). */
export function texteDeLigne(ligne: RecapLigne, action: ActionType = ACTION_NEUTRE): { icone: string; texte: string } {
  const icone = ICONE_CAUSE[ligne.cause];
  // Même exception que `RecapJours` : `rival` est narrative côté moteur (delta toujours 0) ; le test couvre
  // aussi les runs déjà en cours dont le récap vient de l'ancien moteur.
  if (ligne.jauge === null || ligne.cause === "rival") {
    return { icone, texte: phraseNarrative(ligne) };
  }
  const jauge = JAUGE_RECAP[ligne.jauge];
  const detail = detailLisible(ligne);
  const cause = `${libelleCause(ligne.cause, action)}${detail ? ` — ${detail}` : ""}`;
  return { icone, texte: `${cause} : ${deltaLisible(ligne.jauge, ligne.delta)} ${jauge.icone} ${jauge.nom}` };
}

/**
 * Le calcul COMPLET, en blocs de détail : le solde de chaque jauge d'abord (A-16 : jamais seul), puis
 * ce que ça a coûté, ce que le monde a fait pendant ce temps (jour par jour), et ce qu'il en sort.
 * Un bloc sans ligne n'est pas rendu.
 */
export function blocsDuRecap(recap: Recapitulatif, action: ActionType = ACTION_NEUTRE): BlocSource[] {
  const { travail, monde, apres } = sectionner(sansReputation(recap).lignes);
  const jours = parJourCroissant(monde);
  const enLignes = (lignes: readonly RecapLigne[]) => lignes.map((l) => texteDeLigne(l, action));
  const blocs: BlocSource[] = [];

  const soldes = soldesVisibles(recap);
  if (soldes.length > 0) {
    blocs.push({
      titre: "Au total",
      lignes: soldes.map(([jauge, valeur]) => ({
        icone: JAUGE_RECAP[jauge].icone,
        texte: `${deltaLisible(jauge, valeur)} ${JAUGE_RECAP[jauge].nom}`,
      })),
    });
  }
  if (travail.length > 0) blocs.push({ titre: "Ce que ça t'a coûté", lignes: enLignes(travail) });
  if (jours.length === 1) {
    blocs.push({ titre: "Pendant ce temps, le monde a tourné", lignes: enLignes(jours[0]!.lignes) });
  } else {
    for (const groupe of jours) {
      blocs.push({ titre: `Le monde a tourné, jour ${nombre(groupe.jour)}`, lignes: enLignes(groupe.lignes) });
    }
  }
  if (apres.length > 0) blocs.push({ titre: "Au bout du compte", lignes: enLignes(apres) });
  return blocs;
}
